import { useEffect, useRef, useState } from 'react'
import { getAllMembers } from '../lib/supabase'
import { supabase } from '../lib/supabase'
import { verwerkFasekaartVolPromo, haalPromoOntvangsten, promoVoorKaart } from '../lib/promoties'

const MAANDEN = ['jan','feb','mrt','apr','mei','jun','jul','aug','sep','okt','nov','dec']
const FASES = Array.from({ length: 15 }, (_, i) => String(i + 1))

function volgendeFase(huidige) {
  if (huidige === 'aangepast') return 'aangepast'
  const n = parseInt(huidige, 10)
  return n >= 15 ? 'aangepast' : String(n + 1)
}

function faseLabel(fase) {
  return fase === 'aangepast' ? '15+' : `Fase ${fase}`
}

export default function AdminStempelkaartTab() {
  const [members, setMembers] = useState([])
  const [selected, setSelected] = useState(null)
  const [bezoeken, setBezoeken] = useState([])
  const [datum, setDatum] = useState(new Date().toISOString().split('T')[0])
  const [loading, setLoading] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [zoek, setZoek] = useState('')
  const [toonBevestiging, setToonBevestiging] = useState(false)
  const [promoOntvangsten, setPromoOntvangsten] = useState([])
  const topRef = useRef(null)

  useEffect(() => {
    getAllMembers().then(setMembers).catch(() => {})
  }, [])

  useEffect(() => {
    if (!selected) return
    setToonBevestiging(false)
    laadBezoeken(selected)
    laadPromo(selected)
    // Scroll naar boven (zoekbalk + fasekaart) zodat de kaart meteen zichtbaar is
    const t = setTimeout(() => topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60)
    return () => clearTimeout(t)
  }, [selected])

  async function laadBezoeken(memberId) {
    const { data } = await supabase
      .from('bezoeken')
      .select('*')
      .eq('member_id', memberId)
      .order('bezocht_op', { ascending: true })
    setBezoeken(data ?? [])
  }

  async function laadPromo(memberId) {
    try {
      setPromoOntvangsten(await haalPromoOntvangsten(memberId))
    } catch {
      setPromoOntvangsten([])
    }
  }

  async function voegToe() {
    if (!selected || !datum) return
    if (kaartVol) { setToonBevestiging(true); return }
    await plaatsBezoek()
  }

  // faseOverride: gebruikt vlak na het wisselen van fase, zodat het nieuwe bezoek
  // meteen met de juiste (nieuwe) fase wordt getagd i.p.v. de fase van vóór de wissel.
  async function plaatsBezoek(faseOverride) {
    const faseVoorBezoek = faseOverride ?? huidigeFase
    setLoading(true)
    setFeedback('')
    try {
      // Check of datum al bestaat — alleen binnen de fase waar dit bezoek in komt
      // (dezelfde datum kan geldig voorkomen in een eerdere, afgesloten fase)
      const al = bezoeken.find(b => {
        if ((b.fase ?? '1') !== faseVoorBezoek) return false
        const d = new Date(b.bezocht_op)
        const bDatum = d.toISOString().split('T')[0]
        return bDatum === datum
      })
      if (al) { setFeedback('Dit bezoek is al geregistreerd voor deze datum.'); setLoading(false); return }

      // Aantal bezoeken in deze fase vóór dit bezoek — als dit precies het
      // 20e bezoek wordt, is de fasekaart hierna vol.
      const tellingVoorDitBezoek = bezoeken.filter(b => (b.fase ?? '1') === faseVoorBezoek).length

      const { error } = await supabase.from('bezoeken').insert({
        member_id: selected,
        bezocht_op: new Date(datum + 'T12:00:00').toISOString(),
        fase: faseVoorBezoek,
      })
      if (error) throw error
      setFeedback('✓ Bezoek toegevoegd')
      await laadBezoeken(selected)

      if (tellingVoorDitBezoek + 1 >= maximum) {
        try {
          await verwerkFasekaartVolPromo({
            memberId: selected,
            naam: selectedLid?.name,
            fase: faseVoorBezoek,
          })
          await laadPromo(selected)
        } catch (promoErr) {
          setFeedback('✓ Bezoek toegevoegd — let op: attentie-melding kon niet worden verstuurd (' + promoErr.message + ')')
        }
      }
    } catch (err) {
      setFeedback('Fout: ' + err.message)
    } finally {
      setLoading(false)
      setTimeout(() => setFeedback(''), 3000)
    }
  }

  // Wisselt alleen welke fase NIEUWE bezoeken krijgen — raakt nooit bestaande bezoeken,
  // dus terugwisselen naar een eerdere fase toont die kaart weer precies zoals hij was.
  async function wijzigFase(nieuweFase) {
    if (!selected) return
    const { error } = await supabase
      .from('members')
      .update({ fase: nieuweFase })
      .eq('member_id', selected)
    if (error) { setFeedback('Fout: ' + error.message); return }
    setMembers(prev => prev.map(m => m.member_id === selected ? { ...m, fase: nieuweFase } : m))
  }

  async function bevestigVolgendeFase() {
    const volgende = volgendeFase(huidigeFase)
    await wijzigFase(volgende)
    setToonBevestiging(false)
    await plaatsBezoek(volgende)
  }

  async function verwijder(id) {
    await supabase.from('bezoeken').delete().eq('id', id)
    await laadBezoeken(selected)
  }

  async function leegmaken() {
    if (!selected) return
    if (!window.confirm(`${faseLabel(huidigeFase)} helemaal leegmaken? Alleen de bezoeken in deze fase worden verwijderd, eerdere fases blijven staan.`)) return
    setLoading(true)
    setFeedback('')
    try {
      const { error } = await supabase.from('bezoeken').delete().eq('member_id', selected).eq('fase', huidigeFase)
      if (error) throw error
      setFeedback('✓ Fasekaart leeggemaakt')
      await laadBezoeken(selected)
    } catch (err) {
      setFeedback('Fout: ' + err.message)
    } finally {
      setLoading(false)
      setTimeout(() => setFeedback(''), 3000)
    }
  }

  const gefilterd = members.filter(m =>
    m.name?.toLowerCase().includes(zoek.toLowerCase()) ||
    m.member_id?.toLowerCase().includes(zoek.toLowerCase())
  )

  const selectedLid = members.find(m => m.member_id === selected)
  const huidigeFase = selectedLid?.fase ?? '1'
  const faseBezoeken = bezoeken.filter(b => (b.fase ?? '1') === huidigeFase)
  const maximum = 20
  const kaartVol = faseBezoeken.length >= maximum
  const vakjes = Array.from({ length: maximum }, (_, i) => faseBezoeken[i] ?? null)
  const eersteLegeIndex = vakjes.findIndex(v => !v)
  const kaartPromo = kaartVol ? promoVoorKaart(promoOntvangsten, huidigeFase) : null
  const magPromoVakje = !!kaartPromo

  return (
    <div>
      {/* Zoek lid */}
      <div className="card" ref={topRef} style={{ marginBottom: 10, scrollMarginTop: 8 }}>
        <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)', fontFamily: 'Montserrat', marginBottom: 8 }}>
          Fasekaarten — {new Date().getFullYear()}
        </p>
        <input
          value={zoek}
          onChange={e => setZoek(e.target.value)}
          placeholder="Zoek op naam of lidnummer…"
          style={{ marginBottom: 0 }}
        />
      </div>

      {/* Fasekaart van geselecteerd lid — direct onder de zoekbalk */}
      {selected && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
            <p style={{ fontSize: 13, fontWeight: 700, fontFamily: 'Montserrat' }}>
              {members.find(m => m.member_id === selected)?.name}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <select
                value={huidigeFase}
                onChange={e => wijzigFase(e.target.value)}
                style={{ fontSize: 12, fontFamily: 'Montserrat', fontWeight: 600, padding: '4px 8px', borderRadius: 6, border: '1.5px solid var(--border)', color: 'var(--text)', background: 'var(--bg)' }}
              >
                {FASES.map(f => <option key={f} value={f}>{`Fase ${f}`}</option>)}
                <option value="aangepast">15+</option>
              </select>
              <button
                onClick={() => {
                  setSelected(null)
                  setZoek('')
                  setBezoeken([])
                  setFeedback('')
                  setTimeout(() => topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60)
                }}
                style={{ background: 'var(--bg)', border: '1.5px solid var(--border)', borderRadius: 16, padding: '5px 12px', fontSize: 11, fontWeight: 700, fontFamily: 'Montserrat', color: 'var(--green-dark)', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                ↻ Ander lid
              </button>
            </div>
          </div>
          <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 14 }}>
            {faseLabel(huidigeFase)} — {faseBezoeken.length} van {maximum} bezoeken
          </p>

          {/* Raster 4x5 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
            {vakjes.map((bezoek, i) => {
              const isVolgende = !bezoek && i === eersteLegeIndex
              const isPromoVakje = i === maximum - 1 && !!bezoek && magPromoVakje
              return (
              <div
                key={i}
                onClick={isVolgende && !loading ? voegToe : undefined}
                title={isVolgende ? 'Tik om een bezoek toe te voegen' : undefined}
                style={{
                aspectRatio: '1',
                borderRadius: 10,
                background: isPromoVakje ? '#ffffff' : (bezoek ? 'var(--green)' : (isVolgende ? 'var(--green-light)' : 'var(--bg)')),
                border: `2px ${isVolgende ? 'dashed' : 'solid'} ${isPromoVakje ? 'var(--gold)' : (bezoek || isVolgende ? 'var(--green)' : 'var(--border)')}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                padding: 4,
                cursor: isVolgende && !loading ? 'pointer' : 'default',
              }}>
                {bezoek ? (
                  <>
                    <img
                      src={isPromoVakje ? kaartPromo.logoSrc : '/Every_wit.svg'}
                      alt={isPromoVakje ? kaartPromo.naam : ''}
                      style={{ width: isPromoVakje ? '78%' : '72%', height: 'auto', opacity: isPromoVakje ? 1 : 0.92, marginBottom: 2, borderRadius: isPromoVakje ? 4 : 0 }}
                    />
                    <span style={{ fontSize: isPromoVakje ? 8 : 10, color: isPromoVakje ? '#7a611a' : 'rgba(255,255,255,0.9)', fontFamily: 'Montserrat', fontWeight: 700, textAlign: 'center', lineHeight: 1.3, textTransform: isPromoVakje ? 'uppercase' : 'none', letterSpacing: isPromoVakje ? '0.05em' : 'normal' }}>
                      {isPromoVakje ? kaartPromo.vakjeLabel : (() => {
                        const d = new Date(bezoek.bezocht_op)
                        return `${d.getDate()} ${MAANDEN[d.getMonth()]}`
                      })()}
                    </span>
                    <button
                      onClick={() => verwijder(bezoek.id)}
                      style={{
                        position: 'absolute', top: 2, right: 2,
                        background: 'rgba(0,0,0,0.25)', border: 'none',
                        color: '#fff', fontSize: 8, cursor: 'pointer',
                        borderRadius: 4, width: 14, height: 14,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}
                      title="Verwijder bezoek"
                    >✕</button>
                  </>
                ) : isVolgende ? (
                  <span style={{ fontSize: 24, color: 'var(--green)', fontFamily: 'Montserrat', fontWeight: 800, lineHeight: 1 }}>+</span>
                ) : (
                  <span style={{ fontSize: 11, color: 'var(--border)', fontFamily: 'Montserrat', fontWeight: 700 }}>
                    {i + 1}
                  </span>
                )}
              </div>
              )
            })}
          </div>

          {/* Bevestigingsvraag bij volle kaart */}
          {toonBevestiging && (
            <div style={{ background: 'var(--bg)', border: '1.5px solid var(--orange)', borderRadius: 10, padding: 12, marginBottom: 10 }}>
              {kaartPromo && (
                <p style={{ fontSize: 12, fontWeight: 700, color: '#7a611a', background: '#fff8e6', border: '1px solid #f0dfa0', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
                  🎁 Dit lid heeft de {kaartPromo.naam}-attentie verdiend — vergeet niet deze mee te geven bij de balie.
                </p>
              )}
              <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, fontFamily: 'Montserrat' }}>
                {faseLabel(huidigeFase)} is vol (20/20). Doorgaan naar {faseLabel(volgendeFase(huidigeFase))}?
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={bevestigVolgendeFase}
                  disabled={loading}
                  style={{
                    flex: 1, background: 'var(--green)', color: '#fff', border: 'none',
                    borderRadius: 8, padding: '8px', fontWeight: 700, fontSize: 12,
                    cursor: 'pointer', fontFamily: 'Montserrat'
                  }}
                >
                  {loading ? '…' : `Ja, ga door`}
                </button>
                <button onClick={() => setToonBevestiging(false)} className="btn-ghost" style={{ flex: 1 }}>
                  Nee
                </button>
              </div>
            </div>
          )}

          {/* Datum invoeren */}
          {!toonBevestiging && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="date"
                value={datum}
                onChange={e => setDatum(e.target.value)}
                style={{ flex: 1 }}
              />
              <button
                onClick={voegToe}
                disabled={loading}
                style={{
                  background: 'var(--green)', color: '#fff', border: 'none',
                  borderRadius: 9, padding: '10px 16px', fontWeight: 700,
                  fontSize: 13, cursor: 'pointer', fontFamily: 'Montserrat',
                  whiteSpace: 'nowrap'
                }}
              >
                {loading ? '…' : '+ Bezoek'}
              </button>
            </div>
          )}
          <button
            onClick={leegmaken}
            disabled={loading}
            style={{ marginTop: 10, width: '100%', background: '#fff', color: 'var(--red)', border: '1.5px solid var(--red)', borderRadius: 9, padding: '10px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'Montserrat' }}
          >
            {loading ? '…' : 'Kaart leegmaken'}
          </button>
          {feedback && (
            <p style={{ fontSize: 12, marginTop: 8, color: feedback.startsWith('✓') ? 'var(--green-dark)' : 'var(--red)' }}>
              {feedback}
            </p>
          )}
        </div>
      )}

      {/* Ledenlijst — onder de fasekaart */}
      <div className="card">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {gefilterd.map(m => (
            <button
              key={m.member_id}
              onClick={() => setSelected(m.member_id)}
              style={{
                padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: '1.5px solid',
                borderColor: selected === m.member_id ? 'var(--green)' : 'var(--border)',
                background: selected === m.member_id ? 'var(--green)' : 'var(--bg)',
                color: selected === m.member_id ? '#fff' : 'var(--muted)',
                cursor: 'pointer', fontFamily: 'Montserrat'
              }}
            >
              {m.name || m.member_id}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
